import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Check, ArrowRight } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import SuggestPlace from "@/components/SuggestPlace";

const pin = L.divIcon({ className: "", iconSize: [36, 44], iconAnchor: [18, 44], popupAnchor: [0, -40],
  html: '<div style="width:36px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#FF6B9D;border:3px solid #fff;box-shadow:0 6px 14px rgba(25,50,74,.25);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);font-size:16px">🐾</span></div>' });

export default function PlacesMap() {
  useSeo("Chewie & Solo's Favourite Places — Map", "Every bulldog-friendly spot Chewie & Solo have visited, on one map. Plan your Brisbane day out.");
  const [places, setPlaces] = useState(null);
  const [active, setActive] = useState(null);
  const mapRef = useRef(null);
  const leaflet = useRef({ map: null, markers: {} });

  useEffect(() => { api.get("/places").then(({ data }) => setPlaces(data.places)).catch(() => setPlaces([])); }, []);

  useEffect(() => {
    if (!places || !mapRef.current || leaflet.current.map) return;
    // zoom animation disabled: React StrictMode mounts twice and the removed map's pending zoom transition throws
    const map = L.map(mapRef.current, { scrollWheelZoom: false, zoomAnimation: false, fadeAnimation: false }).setView([-27.47, 153.03], 10);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>', maxZoom: 19 }).addTo(map);
    const pts = [];
    places.forEach((p) => {
      const g = p.place.geo;
      if (!g) return;
      const m = L.marker([g.lat, g.lng], { icon: pin }).addTo(map)
        .bindPopup(`<strong style="font-family:inherit">${p.place.name}</strong><br/><span style="font-size:12px;color:#555">${p.place.address || ""}</span><br/><a href="/adventures/${p.slug}" style="color:#FF6B9D;font-weight:700">Read the adventure →</a>`);
      m.on("click", () => setActive(p.slug));
      leaflet.current.markers[p.slug] = m;
      pts.push([g.lat, g.lng]);
    });
    if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40], animate: false });
    else if (pts.length === 1) map.setView(pts[0], 14);
    leaflet.current.map = map;
    return () => { map.remove(); leaflet.current.map = null; };
  }, [places]);

  const focus = (p) => {
    setActive(p.slug);
    const m = leaflet.current.markers[p.slug];
    if (m && leaflet.current.map) { leaflet.current.map.setView(m.getLatLng(), 15, { animate: false }); m.openPopup(); }
  };

  return (
    <div className="bg-[#FFF9EE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#FF6B9D] text-white text-xs font-bold uppercase tracking-[0.18em] px-4 py-1.5"><MapPin className="w-4 h-4" /> Plan a day out</span>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#19324A] leading-[1.05] mt-5" data-testid="places-title">Chewie &amp; Solo's Favourite Places 🗺️</h1>
        <p className="text-base md:text-lg text-[#19324A]/75 mt-4 max-w-xl">Every bulldog-friendly spot our two have sniffed out, on one map. Tap a pin or a card to see why they loved it.</p>
        <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-6 items-start">
          <div ref={mapRef} className="h-[420px] sm:h-[560px] rounded-[2rem] overflow-hidden soft-shadow bg-white z-0" data-testid="places-map" />
          <div className="space-y-3 lg:max-h-[560px] lg:overflow-y-auto lg:pr-1" data-testid="places-list">
            {places === null ? null : places.length === 0 ? (
              <p className="bg-white rounded-3xl p-6 text-sm text-[#19324A]/60 soft-shadow" data-testid="places-empty">No places on the map yet — the first Favourite Places adventure is coming soon.</p>
            ) : places.map((p) => (
              <button key={p.slug} type="button" onClick={() => focus(p)} className={`w-full text-left bg-white rounded-3xl p-4 soft-shadow flex gap-3 transition-all ${active === p.slug ? "ring-2 ring-[#FF6B9D]" : "hover:-translate-y-0.5"}`} data-testid={`place-item-${p.slug}`}>
                {p.cover_image && <img src={p.cover_image} alt="" className="w-20 h-20 rounded-2xl object-cover shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold text-[#19324A] leading-tight">{p.place.name}</p>
                  <p className="text-xs text-[#19324A]/55 mt-1 line-clamp-2">{p.place.address}{p.place.geo?.approx ? " (approx. location)" : ""}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {p.place.dog_friendly && <span className="inline-flex items-center gap-1 rounded-full bg-[#7ED957]/20 text-[#166534] text-[11px] font-bold px-2 py-0.5"><Check className="w-3 h-3" /> Dog-friendly</span>}
                    <Link to={`/adventures/${p.slug}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs font-bold text-[#FF6B9D]" data-testid={`place-read-${p.slug}`}>Read the adventure <ArrowRight className="w-3 h-3" /></Link>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <SuggestPlace />
    </div>
  );
}
